// Purpose: functions related to setting and getting ContentEditorState.selection

// Imports from (only):
// Constants
// Types

import {pipe, identity} from '@packages/utilities/fp/utils';
import {findLast} from '@packages/utilities/array';
import {
  flatten,
  getChildren,
  getElementWithAncestors,
  getTag,
  getTagFromData,
  getClosestListElement,
  removeElementById,
  isUserComponent,
} from '@packages/utilities/platform';
import {
  isEmphasized,
  isFigure,
  isHtmlEmbed,
  isCodeBlock,
  isLineBreakElement,
  isLinkElement,
  isList,
  isStringElement,
  isStrong,
  isTextHolder,
  isBlockquote,
  isSubscript,
  isSuperscript,
  isInlineCode,
  isSpan,
  isHeading,
} from '@packages/utilities/elementTypeUtils';
import {Expressions} from '@packages/systems/wfdl';
import {EElement} from '@packages/systems/wfdl-ast';
import {create as createBox, equal as equalBox} from '@packages/utilities/box';
import {
  NodeSelectionRecord,
  RangeSelectionRecord,
} from '@packages/shared/records';

import {
  DEFAULT_FORMAT_TOOLBAR_BUTTONS,
  FORMAT_BUTTONS,
  FormatToolbarButtonsType,
} from './constants';

// types
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {
  NodeSelectionRecordType,
  RangeSelectionRecordType,
  SelectionRecordType,
} from '@packages/shared/records';
import {ContentEditorActive} from '@packages/shared/states';
import type {
  NodeSelectionType,
  ParsedSelectionType,
  RangeSelectionType,
} from '@packages/shared/types/ContentEditor';
import type {Box} from '@packages/utilities/box';

export const setSelection =
  (selection: SelectionRecordType) => (state: ContentEditorActive) =>
    state.set('selection', selection);

export const updateSelection =
  (updater: (sel: SelectionRecordType) => SelectionRecordType) =>
  (state: ContentEditorActive) =>
    state.update('selection', updater);

export const setRangeSelection =
  ({
    anchorNodeId,
    focusNodeId,
    anchorOffset,
    focusOffset,
    leftToRight,
    rangeClientRect,
  }: Partial<{
    anchorNodeId: string;
    focusNodeId: string;
    anchorOffset: number;
    focusOffset: number;
    leftToRight: boolean;
    rangeClientRect?: Box | null;
  }>) =>
  (state: ContentEditorActive) => {
    const rangeSelection = assertRangeSelectionRecordType(state.selection);
    if (!rangeSelection) return state;

    const updatedSelection: RangeSelectionRecordType = RangeSelectionRecord({
      anchorNodeId: anchorNodeId ?? rangeSelection.anchorNodeId,
      focusNodeId: focusNodeId ?? rangeSelection.focusNodeId,
      anchorOffset: anchorOffset ?? rangeSelection.anchorOffset,
      focusOffset: focusOffset ?? rangeSelection.focusOffset,
      leftToRight: leftToRight ?? rangeSelection.leftToRight,
      rangeClientRect: rangeClientRect ?? rangeSelection.rangeClientRect,
    });

    return state.set('selection', updatedSelection);
  };

export const selectStartOfString =
  (stringNodeId: string) => (state: ContentEditorActive) => {
    return setRangeSelection({
      anchorNodeId: stringNodeId,
      focusNodeId: stringNodeId,
      anchorOffset: 0,
      focusOffset: 0,
    })(state);
  };

export const capSelectionOffsetToValueLength =
  (value: string, textNodeId: string) => (selection: SelectionRecordType) => {
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return selection;

    let newSelection = rangeSelection;

    if (newSelection.anchorNodeId === textNodeId) {
      newSelection = newSelection.set(
        'anchorOffset',
        Math.min(value.length, newSelection.anchorOffset)
      );
    }

    if (newSelection.focusNodeId === textNodeId) {
      newSelection = newSelection.set(
        'focusOffset',
        Math.min(value.length, newSelection.focusOffset)
      );
    }

    return newSelection;
  };

export const reifySelectionRecord = (
  parsedSelection: ParsedSelectionType
): SelectionRecordType => {
  if ('nodeId' in parsedSelection && parsedSelection.nodeId) {
    // Reifies a selection record from a figure selection
    const {nodeId, nodeClientRect} = parsedSelection;

    return NodeSelectionRecord({
      nodeId,
      nodeClientRect: nodeClientRect ? createBox(nodeClientRect) : null,
    });
  } else if (
    'anchorNodeId' in parsedSelection &&
    parsedSelection.anchorNodeId
  ) {
    // Reifies a selection record from a node selection
    const {
      anchorNodeId,
      focusNodeId,
      anchorOffset,
      focusOffset,
      rangeClientRect,
      leftToRight,
    } = parsedSelection;
    // Reifies a selection record from a native anchor/focus node selection
    return RangeSelectionRecord({
      anchorNodeId,
      focusNodeId,
      anchorOffset,
      focusOffset,
      leftToRight,
      rangeClientRect: rangeClientRect ? createBox(rangeClientRect) : null,
    });
  } else if (parsedSelection.type === 'Node' && !parsedSelection.nodeId) {
    // Reifies a selection record from a link node in link-only mode for the
    // editor
    const {nodeClientRect} = parsedSelection;

    return NodeSelectionRecord({
      nodeClientRect: nodeClientRect ? createBox(nodeClientRect) : null,
    });
  } else {
    throw new Error('parsedSelection was not a valid node or range selection');
  }
};

/**
 * Checks if the caret (text cursor) is positioned at the start of a text node
 */
export const caretIsAtStartOfTextNode =
  (selection: SelectionRecordType) =>
  (state: ContentEditorActive): boolean => {
    const {subtree} = state;
    const rangeSelection: RangeSelectionRecordType | null =
      assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return false;

    const anchorNode = Expressions.getElementById(
      subtree.data,
      rangeSelection.anchorNodeId
    );

    if (!anchorNode) return false;

    const text = Expressions.getText(anchorNode.data);
    return text !== null && rangeSelection.anchorOffset === 0;
  };

/**
 * Checks if the caret (text cursor) is positioned at the end of a text node
 */
export const caretIsAtEndOfTextNode =
  (selection: SelectionRecordType) =>
  (state: ContentEditorActive): boolean => {
    const {subtree} = state;
    const rangeSelection: RangeSelectionRecordType | null =
      assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return false;

    const anchorNode = Expressions.getElementById(
      subtree.data,
      rangeSelection.anchorNodeId
    );

    if (!anchorNode) return false;

    const text = Expressions.getText(anchorNode.data);
    const endOffset = text?.length ?? 0;
    return text !== null && rangeSelection.anchorOffset === endOffset;
  };

export const caretIsInStringElement = (state: ContentEditorActive): boolean => {
  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (rangeSelection === null || !isCollapsed(rangeSelection)) return false;
  const anchorNode = Expressions.getElementById(
    EElement(subtree),
    rangeSelection.anchorNodeId
  );

  return anchorNode ? isStringElement(anchorNode) : false;
};

export const getClientRect = (
  selection: SelectionRecordType | ParsedSelectionType
): Box | null =>
  assertNodeSelectionType(selection)?.nodeClientRect ||
  assertRangeSelectionType(selection)?.rangeClientRect ||
  null;

//  selection of text with caret - this is the POJO that is used in payloads other than ContentEditorState before being made into a Record. Use assertRangeSelectionRecordType if selection is from ContentEditorState.selection.
export const assertRangeSelectionType = (
  selection: ParsedSelectionType
): RangeSelectionType | null => {
  if (selection.type === 'Range') {
    return selection as RangeSelectionType;
  }
  return null;
};

//  selection of text with caret - this is the immutableJs record type in the ContentEditorState.selection
export const assertRangeSelectionRecordType = (
  selection: SelectionRecordType
): RangeSelectionRecordType | null => {
  if (selection.type === 'Range' && 'set' in selection) {
    return selection as RangeSelectionRecordType;
  }
  return null;
};

// selection of node (e.g. code block, figure etc) - this is the POJO that is used in payloads other than ContentEditorState before being made into a Record. Use assertNodeSelectionRecordType if selection is from ContentEditorState.selection
export const assertNodeSelectionType = (
  selection: ParsedSelectionType
): NodeSelectionType | null => {
  if (selection.type === 'Node') {
    return selection as NodeSelectionType;
  }
  return null;
};

// selection of node (e.g. code block, figure etc) -  this is the type in the ContentEditorState.selection
export const assertNodeSelectionRecordType = (
  selection: SelectionRecordType
): NodeSelectionRecordType | null => {
  if (selection.type === 'Node' && 'set' in selection) {
    return selection as NodeSelectionRecordType;
  }
  return null;
};

export const getNodeIdFromSelection = (selection: SelectionRecordType) => {
  if (selection.type === 'Range') {
    return selection.anchorNodeId;
  }

  // selection.type is Node
  return selection?.nodeId || null;
};

export const getNodeIdFromSelectionAtLeft = (
  selection: SelectionRecordType
): string | null => {
  if (selection.type === 'Range') {
    return selection.leftToRight
      ? selection.anchorNodeId
      : selection.focusNodeId;
  }

  // selection.type is Node
  return selection.nodeId ?? null;
};

export const getNodeIdFromSelectionAtRight = (
  selection: SelectionRecordType
): string | null => {
  if (selection.type === 'Range') {
    return selection.leftToRight
      ? selection.focusNodeId
      : selection.anchorNodeId;
  }

  // selection.type is Node
  return selection.nodeId ?? null;
};

export const selectNode = (nodeId: string) => (state: ContentEditorActive) => {
  return state.set('selection', NodeSelectionRecord({nodeId}));
};

// the start and end of the users selection is within the same node
export const selectionWithinSameNode = (selection: SelectionRecordType) => {
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) {
    return false;
  }

  return rangeSelection.anchorNodeId === rangeSelection.focusNodeId;
};

export const collapseTextSelection = (
  selection: RangeSelectionRecordType,
  textNodeId: string,
  leftOffset: number
): RangeSelectionRecordType => {
  const newSelectionRecord = RangeSelectionRecord({
    anchorNodeId: textNodeId,
    anchorOffset: leftOffset,
    focusNodeId: textNodeId,
    focusOffset: leftOffset,
    leftToRight: true,
  });

  if (selection.rangeClientRect) {
    return newSelectionRecord.set('rangeClientRect', selection.rangeClientRect);
  }
  return newSelectionRecord;
};

export const setClientRect =
  (rect: null | Box) => (selection: SelectionRecordType) => {
    if (selection.type === 'Range')
      return selection.set('rangeClientRect', rect);
    if (selection.type === 'Node') return selection.set('nodeClientRect', rect);
    return selection;
  };

export const setSelectionAndClearClientRect = (
  selection: SelectionRecordType
) => setSelection(setClientRect(null)(selection));

export const collapseTextSelectionAtLeft = (
  selection: RangeSelectionRecordType
): RangeSelectionRecordType => {
  const {anchorNodeId, focusNodeId, anchorOffset, focusOffset, leftToRight} =
    selection;
  const leftNodeId = leftToRight ? anchorNodeId : focusNodeId;
  const leftOffset = leftToRight ? anchorOffset : focusOffset;

  return collapseTextSelection(selection, leftNodeId, leftOffset);
};

export const isStringOrLineBreak = (element: ExpressionElement) =>
  isStringElement(element) || isLineBreakElement(element);

const getEdgeElementFromCaretOffset = (
  caretOffset: number,
  siblings: ExpressionElement[],
  edge: 'left' | 'right',
  subtree: ExpressionElement
): ExpressionElement | undefined => {
  const allElements = flatten(subtree);

  if (edge === 'right') {
    if (caretOffset === 0) {
      const rightElementOutsideSelectionId = siblings[0]?.id;

      if (rightElementOutsideSelectionId) {
        const index = allElements.findIndex(
          (el) => el.id === rightElementOutsideSelectionId
        );
        if (index > 0) {
          return allElements
            .slice(0, index)
            .reverse()
            .find(isStringOrLineBreak);
        }
      }
    } else {
      return siblings[caretOffset - 1];
    }
  } else if (edge === 'left') {
    if (caretOffset === siblings.length) {
      const leftElementOutsideSelectionId = siblings[siblings.length - 1]?.id;

      if (leftElementOutsideSelectionId) {
        const index = allElements.findIndex(
          (el) => el.id === leftElementOutsideSelectionId
        );
        if (index < allElements.length - 1) {
          return allElements.slice(index + 1).find(isStringOrLineBreak);
        }
      }
    } else {
      return siblings[caretOffset];
    }
  }

  return undefined;
};

// needed as user can select text left to right or right to left.
export const getLeftRightPosition = (
  selection: RangeSelectionRecordType
): {
  leftNodeId: string;
  rightNodeId: string;
  leftOffset: number;
  rightOffset: number;
} => {
  const {anchorNodeId, focusNodeId, anchorOffset, focusOffset, leftToRight} =
    selection;

  const leftNodeId = leftToRight ? anchorNodeId : focusNodeId;
  const rightNodeId = leftToRight ? focusNodeId : anchorNodeId;
  const leftOffset = leftToRight ? anchorOffset : focusOffset;
  const rightOffset = leftToRight ? focusOffset : anchorOffset;

  return {
    leftNodeId,
    rightNodeId,
    leftOffset,
    rightOffset,
  };
};

const getChildAtCaretOffset = (
  subtree: ExpressionElement,
  edgeElement: ExpressionElement,
  caretOffset: number,
  edge: 'left' | 'right'
): ExpressionElement => {
  // is textHolder (e.g. paragraph) means that the edge must be in a linebreak that is a child of the textHolder, else a text node itself would be selected.
  if (isTextHolder(edgeElement) || edgeElement.id === subtree.id) {
    const childrenInEdgeOffsetElement = getChildren(edgeElement);
    if (childrenInEdgeOffsetElement === undefined) return edgeElement;
    const deepestEdgeSelectedElement = getEdgeElementFromCaretOffset(
      caretOffset,
      childrenInEdgeOffsetElement,
      edge,
      subtree
    );

    return deepestEdgeSelectedElement || edgeElement;
  }
  return edgeElement;
};

export const moveRightSideOfSelection = (
  delta: number,
  selection: RangeSelectionRecordType
) => {
  const {anchorOffset, focusOffset, leftToRight} = selection;

  const currentOffset = leftToRight ? focusOffset : anchorOffset;
  const rightSideKey = leftToRight ? 'focusOffset' : 'anchorOffset';
  const newOffset = currentOffset + delta;

  return selection.set(rightSideKey, newOffset);
};

export const setLeftEndOfSelection =
  (nodeId: string, offset: number) => (selection: RangeSelectionRecordType) => {
    const {leftToRight} = selection;

    const nodeIdKey = leftToRight ? 'anchorNodeId' : 'focusNodeId';
    const offsetKey = leftToRight ? 'anchorOffset' : 'focusOffset';

    return selection.set(nodeIdKey, nodeId).set(offsetKey, offset);
  };

export const setRightEndOfSelection =
  (nodeId: string, offset: number) => (selection: RangeSelectionRecordType) => {
    const {leftToRight} = selection;

    const nodeIdKey = leftToRight ? 'focusNodeId' : 'anchorNodeId';
    const offsetKey = leftToRight ? 'focusOffset' : 'anchorOffset';

    return selection.set(nodeIdKey, nodeId).set(offsetKey, offset);
  };

export const keepSelectionEdgeNearLineBreak =
  (edge: 'left' | 'right', oldState: ContentEditorActive) =>
  (newState: ContentEditorActive) => {
    const oldSelection = assertRangeSelectionRecordType(oldState.selection);
    const newSelection = assertRangeSelectionRecordType(newState.selection);
    if (!oldSelection || !newSelection) return newState;
    // Get the left/right nodes of the selection before we transformed it
    const oldLeftRightPosition = getLeftRightPosition(oldSelection);
    const edgeNodeId =
      oldLeftRightPosition[edge === 'left' ? 'leftNodeId' : 'rightNodeId'];
    const edgeOffset =
      oldLeftRightPosition[edge === 'left' ? 'leftOffset' : 'rightOffset'];
    const edgeNode = Expressions.getElementById(
      oldState.subtree.data,
      edgeNodeId
    );
    if (!edgeNode || isStringElement(edgeNode)) return newState;
    const edgeNodeChildren = getChildren(edgeNode);
    if (!edgeNodeChildren) return newState;
    // If we're working on the right edge of the selection the caret offset will
    // be just to the right of the line break that moved.
    const offsetDelta = edge === 'right' ? 1 : 0;

    // Note: Sometimes the line break moved to its parent as the format node
    // we're unwrapping unpacked, in other cases the format node is re-creted
    // with a new id as the algo steps through and splits nodes.
    const lineBreakThatMoved = edgeNodeChildren[edgeOffset - offsetDelta];
    if (!lineBreakThatMoved) return newState;
    const newParent = Expressions.getParent(
      EElement(newState.subtree),
      lineBreakThatMoved.id
    );
    if (!newParent) return newState;
    const newParentChildren = getChildren(newParent) || [];
    const newChildIndex = newParentChildren.findIndex(
      (el) => el.id === lineBreakThatMoved.id
    );
    const newSelectionEdgeIndex = newChildIndex + offsetDelta;
    const setSelectionEndFunc =
      edge === 'left' ? setLeftEndOfSelection : setRightEndOfSelection;

    if (edgeNodeId !== newParent.id || edgeOffset !== newSelectionEdgeIndex)
      return newState.set(
        'selection',
        setSelectionEndFunc(newParent.id, newSelectionEdgeIndex)(newSelection)
      );

    return newState;
  };
// This is used after a subtree change that moves line breaks at the edges of a
// selection (eg: when wrapping/unwrapping text/line breaks in format nodes).
// Because textNode-less range positions (when the selection edge is between
// line breaks) are based on the line-break's parent and offset. This looks at
// the old state to find out which line break the caret was next to on the inner
// edges of the selection, and moves the caret to be relative to that line break
// in the new state which could have completely different hierarchy and parent.
export const moveSelectionEdgesWithLineBreaks = (
  oldState: ContentEditorActive
) =>
  pipe([
    keepSelectionEdgeNearLineBreak('left', oldState),
    keepSelectionEdgeNearLineBreak('right', oldState),
  ]);

// The left and/or right edge of the selection can each be between two line
// breaks where those two line breaks are nested differently. So for example you
// could describe the left edge in this case as being the last index of the one
// line break's parent, or the 0 index of the other.
//
// This Util makes sure in those cases that the selection edges are defined as
// being adjacent to the line breaks on the 'inside' of the selection.
//
// This lets us better keep track and move the selection edges as we tend to
// operate on nodes 'inside' the selection in various algos that wrap/unwrap and
// merge nodes and they get moved around the tree.
export const bumpSelectionEdgesInside = (state: ContentEditorActive) => {
  const {subtree, selection} = state;

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (rangeSelection === null) return state;
  // These are the deepest textLevel nodes. They'll either be textNodes or line
  // breaks
  const elements = getDeepestSelectedTextLevelNode(subtree, rangeSelection);

  if (!elements) return state;
  const {leftElement, rightElement} = elements;

  let newSelection = rangeSelection;

  const newSelLeftElement = isStringElement(leftElement)
    ? leftElement
    : (Expressions.getParent(EElement(subtree), leftElement.id) ?? leftElement);
  const newSelRightElement = isStringElement(rightElement)
    ? rightElement
    : (Expressions.getParent(EElement(subtree), rightElement.id) ??
      rightElement);

  // These correspond to the state.selection.anchor/Focus element id. It'll
  // either be a String element, or a line break's *parent*.
  const {leftNodeId: selLeftElementId, rightNodeId: selRightElementId} =
    getLeftRightPosition(rangeSelection);

  if (selLeftElementId !== newSelLeftElement.id) {
    newSelection = setLeftEndOfSelection(newSelLeftElement.id, 0)(newSelection);
  }

  if (selRightElementId !== newSelRightElement.id) {
    const newRightOffset = isStringElement(newSelRightElement)
      ? (Expressions.getText(newSelRightElement.data)?.length ?? 0)
      : (getChildren(newSelRightElement)?.length ?? 0);
    newSelection = setRightEndOfSelection(
      newSelRightElement.id,
      newRightOffset
    )(newSelection);
  }

  return state.set('selection', newSelection);
};

export const findLeftElement =
  (predicate: (el: ExpressionElement) => boolean) =>
  (root: ExpressionElement) =>
    flatten(root).find(predicate) ?? null;

export const findRightElement =
  (predicate: (el: ExpressionElement) => boolean) =>
  (root: ExpressionElement) =>
    findLast(flatten(root), predicate) ?? null;

const findLeftStringElement = findLeftElement(isStringElement);
const findRightStringElement = findRightElement(isStringElement);

// eslint-disable-next-line webflow/selector-memoization
export const selectLinkElement = (link: ExpressionElement) => {
  const leftNode = findLeftStringElement(link);
  const rightNode = findRightStringElement(link);

  if (!leftNode || !rightNode) return identity;

  return setSelection(
    RangeSelectionRecord({
      anchorNodeId: leftNode.id,
      focusNodeId: rightNode.id,
      anchorOffset: 0,
      focusOffset: (Expressions.getText(rightNode.data) ?? '').length,
      leftToRight: true,
    })
  );
};

// Note: We want to maintain the left/right position
// eslint-disable-next-line webflow/selector-memoization
export const selectText = (elementId: string, text: string) =>
  pipe([
    setLeftEndOfSelection(elementId, 0),
    setRightEndOfSelection(elementId, text.length),
  ]);

// If the left or right selection is not in a text node (because it's next to
// a line break), it means that we are not getting the actual deepest selected
// node on that side (this is because a selection on a linebreak is actually the containing element)
// we need to dig deeper to get the linebreak / text nodes that are selected.
export const getDeepestSelectedTextLevelNode = (
  subtree: ExpressionElement,
  selection: RangeSelectionRecordType
): {
  leftElement: ExpressionElement;
  rightElement: ExpressionElement;
} | null => {
  const {leftNodeId, leftOffset, rightNodeId, rightOffset} =
    getLeftRightPosition(selection);

  const leftSelectedElement = Expressions.getElementById(
    EElement(subtree),
    leftNodeId
  );

  const rightSelectedElement = Expressions.getElementById(
    EElement(subtree),
    rightNodeId
  );

  if (leftSelectedElement === null || rightSelectedElement === null)
    return null;

  const leftElement = getChildAtCaretOffset(
    subtree,
    leftSelectedElement,
    leftOffset,
    'left'
  );

  if (leftElement === null) return null;

  const rightElement = getChildAtCaretOffset(
    subtree,
    rightSelectedElement,
    rightOffset,
    'right'
  );

  if (rightElement === null) return null;

  return {
    leftElement,
    rightElement,
  };
};

/**
 * Removes nodes within a selection range while preserving specific boundary nodes and their ancestors.
 *
 * @param leftNodeId - ID of the left boundary node of the selection
 * @param rightNodeId - ID of the right boundary node of the selection
 * @param includeLeftNode - Whether to remove the left boundary node
 * @param includeRightNode - Whether to remove the right boundary node
 *
 * This function:
 * 1. Flattens the subtree to work with nodes linearly
 * 2. Identifies nodes between left and right boundaries (inclusive)
 * 3. Preserves the right node's ancestors to maintain tree structure
 * 4. Selectively removes nodes based on:
 *    - Whether they're ancestors of the right node (preserved)
 *    - Whether they're boundary nodes (controlled by include flags)
 *    - All other nodes in between are removed
 */
export const trimNodesInsideSelection =
  (
    leftNodeId: string,
    rightNodeId: string,
    includeLeftNode: boolean,
    includeRightNode: boolean
  ) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const flatSubtree = flatten(subtree);
    const subtreeExpr = EElement(subtree);

    const leftNode = Expressions.getElementById(subtreeExpr, leftNodeId);

    const rightNode = Expressions.getElementById(subtreeExpr, rightNodeId);

    if (!leftNode || !rightNode) return subtree;

    const leftIndex = flatSubtree.findIndex((el) => el.id === leftNodeId);
    const rightIndex = flatSubtree.findIndex((el) => el.id === rightNodeId);

    if (leftIndex === -1 || rightIndex === -1) return subtree;

    // Get all elements between left and right (inclusive)
    const elementsBetween = flatSubtree.slice(leftIndex, rightIndex + 1);

    const rightAnchor = isStringElement(rightNode)
      ? rightNode
      : Expressions.getParent(subtreeExpr, rightNodeId);

    if (!rightAnchor) return subtree;

    // Get ancestors of right node to preserve
    const rightAnchorAndAncestors = new Set(
      getElementWithAncestors(subtreeExpr)(rightAnchor.id).map(
        (node) => node.id
      )
    );

    // Filter out elements we want to keep
    const elementsToRemove = elementsBetween.filter((element) => {
      // Don't remove ancestors of the right node
      if (rightAnchorAndAncestors.has(element.id)) {
        return false;
      }

      // Handle left and right boundary nodes based on include flags
      if (element.id === leftNodeId && !includeLeftNode) {
        return false;
      }
      if (element.id === rightNodeId && !includeRightNode) {
        return false;
      }

      return true;
    });

    // Remove all marked elements
    return (
      Expressions.getElement(
        elementsToRemove.reduce(
          (acc, element) => removeElementById(acc, element.id),
          subtreeExpr
        )
      ) || subtree
    );
  };
// lists all the elements within the bounds of the selection
// First uses set + depth to find the elements from the subtree down to the descendant at the start
// of the selection, then adds all the element it finds until the end of the selection.
export const getSelectedElements = (
  subtree: ExpressionElement,
  rangeSelection?: RangeSelectionRecordType | null
): ExpressionElement[] => {
  if (!rangeSelection) {
    return [];
  }

  const flatSubtree = flatten(subtree);
  const elements = getDeepestSelectedTextLevelNode(subtree, rangeSelection);

  if (elements === null) return [];

  const {leftElement, rightElement} = elements;

  if (leftElement === null || rightElement === null) return [];

  const parentElement = Expressions.getParent(
    EElement(subtree),
    leftElement.id
  );

  if (parentElement === null) return [];

  // Note: We slice off the first because it's the root of the subtree ie: the rich text div
  const ancestorsOfLeftElement = getElementWithAncestors(EElement(subtree))(
    parentElement.id
  ).slice(1);
  // Note: This includes the rightNode's ancestors and the left/right node's uncles along the inside edge of the rangeSelection
  const elementsBetweenWithRightElement = flatSubtree.slice(
    flatSubtree.indexOf(leftElement),
    flatSubtree.indexOf(rightElement) + 1
  );

  return [...ancestorsOfLeftElement, ...elementsBetweenWithRightElement];
};

// gets all elements the user has selected then filters out paragraph etc to just get the strings and line breaks.
export const getSelectedTextAndLineBreakNodes = (
  subtree: ExpressionElement,
  rangeSelection: RangeSelectionRecordType
): ExpressionElement[] => {
  return getSelectedElements(subtree, rangeSelection).filter(
    isStringOrLineBreak
  );
};

export const getFormatButtonType = (
  element: ExpressionElement
): keyof FormatToolbarButtonsType | undefined => {
  if (isStrong(element)) {
    return 'bold';
  }
  if (isEmphasized(element)) {
    return 'italic';
  }
  if (isLinkElement(element)) {
    return 'link';
  }
  if (isBlockquote(element)) {
    return 'blockquote';
  }
  if (isSuperscript(element)) {
    return 'superscript';
  }
  if (isSubscript(element)) {
    return 'subscript';
  }
  if (isInlineCode(element)) {
    return 'inlineCode';
  }
  if (isSpan(element)) {
    return 'span';
  }

  if (isHeading(element)) {
    const tag = getTagFromData(element);

    if (tag === 'h1') {
      return 'h1';
    }
    if (tag === 'h2') {
      return 'h2';
    }
    if (tag === 'h3') {
      return 'h3';
    }
    if (tag === 'h4') {
      return 'h4';
    }
    if (tag === 'h5') {
      return 'h5';
    }
    if (tag === 'h6') {
      return 'h6';
    }
  }

  if (isList(element)) {
    const tag = getTagFromData(element);

    if (tag === 'ol') {
      return 'ol';
    }
    if (tag === 'ul') {
      return 'ul';
    }
  }

  return undefined;
};

// determines if the selection is a candidate for wrapping i.e. it has elements that aren't already wrapped in that element.
// For example, with the predicate isStrong:
// "something <b> bold </b>" - would wrap be a wrap candidate because the string element 'something' is not wrapped.
// "<b><em> bold</em></b>" - is not a wrap candidate for isStrong predicate because every element is already a wrapped in <b>
export const selectionHasTextLevelNodesToWrap = (
  subtree: ExpressionElement,
  rangeSelection: RangeSelectionRecordType,
  ancestorPredicate: (element: ExpressionElement) => boolean
): boolean => {
  const selectedTextLevelElements = getSelectedTextAndLineBreakNodes(
    subtree,
    rangeSelection
  );

  if (selectedTextLevelElements.length) {
    return Boolean(
      selectedTextLevelElements.find((textLevelElement: ExpressionElement) => {
        return !Expressions.closestElement(
          EElement(subtree),
          textLevelElement.id,
          ancestorPredicate
        );
      })
    );
  }

  return false;
};

// used in the format toolbar when a selection to know what buttons should be shown as active (e.g. if selection contains italic text, italic buttons is active)
export const getActiveButtonsFromSelectedElements = (
  subtree: ExpressionElement,
  selection: SelectionRecordType
): FormatToolbarButtonsType => {
  const rangeSelection = assertRangeSelectionRecordType(selection);

  // First set the buttons based on what tags exist in the selection
  const activeButtons = rangeSelection
    ? getSelectedElements(subtree, rangeSelection).reduce(
        (_activeButtons, element: ExpressionElement) => {
          const button = getFormatButtonType(element);

          if (button && !_activeButtons[button]) {
            const clearActive =
              _activeButtons.clear || FORMAT_BUTTONS.hasOwnProperty(button);

            _activeButtons[button] = true;
            _activeButtons.clear = clearActive;
            return _activeButtons;
          }

          return _activeButtons;
        },
        {...DEFAULT_FORMAT_TOOLBAR_BUTTONS}
      )
    : DEFAULT_FORMAT_TOOLBAR_BUTTONS;

  // only show as active the closest list, in the case of nested lists this is important
  const listElement =
    rangeSelection &&
    getClosestListElement(EElement(subtree), rangeSelection.anchorNodeId);

  if (listElement) {
    const tag = getTagFromData(listElement);

    if (tag === 'ol') {
      activeButtons.ol = true;
      activeButtons.ul = false;
    }
    if (tag === 'ul') {
      activeButtons.ol = false;
      activeButtons.ul = true;
    }
  }

  // We want to allow extending bold/italic if part of the selection covers a
  // format and part of it covers nodes that can be wrapped.
  return {
    ...activeButtons,
    bold:
      activeButtons.bold &&
      !(
        rangeSelection &&
        selectionHasTextLevelNodesToWrap(subtree, rangeSelection, isStrong)
      ),
    italic:
      activeButtons.italic &&
      !(
        rangeSelection &&
        selectionHasTextLevelNodesToWrap(subtree, rangeSelection, isEmphasized)
      ),
    superscript:
      activeButtons.superscript &&
      !(
        rangeSelection &&
        selectionHasTextLevelNodesToWrap(subtree, rangeSelection, isSuperscript)
      ),
    subscript:
      activeButtons.subscript &&
      !(
        rangeSelection &&
        selectionHasTextLevelNodesToWrap(subtree, rangeSelection, isSubscript)
      ),
    inlineCode:
      activeButtons.inlineCode &&
      !(
        rangeSelection &&
        selectionHasTextLevelNodesToWrap(subtree, rangeSelection, isInlineCode)
      ),
  };
};

export const isCollapsed = (
  rangeSelection: RangeSelectionRecordType
): boolean => {
  const {anchorNodeId, focusNodeId, anchorOffset, focusOffset} = rangeSelection;
  return anchorNodeId === focusNodeId && anchorOffset === focusOffset;
};

export const getCharBeforeCaret = (
  subtree: ExpressionElement,
  selection: RangeSelectionRecordType
) => {
  const {leftNodeId, leftOffset} = getLeftRightPosition(selection);
  const leftElement = Expressions.getElementById(EElement(subtree), leftNodeId);
  const value = leftElement ? Expressions.getText(leftElement.data) : '';
  return value ? value.charAt(leftOffset - 1) : '';
};

export const getCharAfterCaret = (
  subtree: ExpressionElement,
  selection: RangeSelectionRecordType
) => {
  const {rightNodeId, rightOffset} = getLeftRightPosition(selection);
  const rightElement = Expressions.getElementById(
    EElement(subtree),
    rightNodeId
  );
  const value = rightElement ? Expressions.getText(rightElement.data) : '';
  return value ? value.charAt(rightOffset) : '';
};

// delta should be a positive number to move it to the right, negative number to move it to the left
const changeCaretOffset = (
  selection: RangeSelectionRecordType,
  delta: number
) => {
  const updater = (offset: number) => offset + delta;

  return selection
    .update('anchorOffset', updater)
    .update('focusOffset', updater);
};

export const changeCaretOffsetBy =
  (delta: number) => (state: ContentEditorActive) => {
    const {selection} = state;
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (rangeSelection === null) return state;
    if (!isCollapsed(rangeSelection)) {
      if (process.env.NODE_ENV === 'development') {
        console.error(
          'changeCaretOffsetBy should only be called on a collapsed selection'
        );
      }
      return state;
    }
    return state.set('selection', changeCaretOffset(rangeSelection, delta));
  };

// We want to compare two selections to determine if the caret has actually
// moved or if we've changed from a node to range or visa-versa, while ignoring
// differences in clientRects
export const hasCaretMoved = (
  selection1?: SelectionRecordType | ParsedSelectionType,
  selection2?: SelectionRecordType | ParsedSelectionType
): boolean => {
  if (!selection1 && !selection2) {
    return false;
  } else if (!selection1 || !selection2) {
    return true;
  }

  if (selection1.type !== selection2.type) {
    return true;
  }

  if (
    selection1.type === 'Node' &&
    selection2.type === 'Node' &&
    selection1.nodeId !== selection2.nodeId
  ) {
    return true;
  }

  if (
    selection1.type === 'Range' &&
    selection2.type === 'Range' &&
    (selection1.anchorNodeId !== selection2.anchorNodeId ||
      selection1.focusNodeId !== selection2.focusNodeId ||
      selection1.anchorOffset !== selection2.anchorOffset ||
      selection1.focusOffset !== selection2.focusOffset)
  ) {
    return true;
  }

  return false;
};

export const clientRectChanged = (rect1?: Box, rect2?: Box | null): boolean => {
  if (!rect1 && !rect2) {
    return false;
  }

  if (!rect1 || !rect2) {
    return true;
  }

  return !equalBox(rect1, rect2);
};

export const isEmbedSelected = (
  selectedElements: Array<ExpressionElement>
): boolean => {
  const el = selectedElements[0];
  return Boolean(el && selectedElements.length === 1 && isHtmlEmbed(el));
};

export const isCodeBlockSelected = (
  selectedElements: Array<ExpressionElement>
): boolean => {
  const el = selectedElements[0];
  return Boolean(el && selectedElements.length === 1 && isCodeBlock(el));
};

export const isFigureSelected = (
  selectedElements: Array<ExpressionElement>
): boolean => {
  const el0 = selectedElements[0];
  const el1 = selectedElements[1];
  const el2 = selectedElements[2];
  const len = selectedElements.length;
  return Boolean(
    (len === 2 && el0 && el1 && isFigure(el0) && getTag(el1) === 'div') ||
    (len === 1 && el0 && isFigure(el0)) ||
    (len === 3 &&
      el0 &&
      el1 &&
      el2 &&
      isFigure(el0) &&
      isLinkElement(el1) &&
      getTag(el2) === 'div')
  );
};

export const isUserComponentSelected = (
  selectedElements: Array<ExpressionElement>
): boolean => {
  const el = selectedElements[0];
  return Boolean(
    el && selectedElements.length === 1 && isUserComponent(el.type)
  );
};

// In Safari, sometimes image figures specifically will appear as a caret
// selection with the root rich text element as the anchorNode
// and the figure Node as the offset
export const getSelectedFigureSafariFallback = (
  subtree: ExpressionElement,
  selection: RangeSelectionRecordType
): ExpressionElement | null => {
  if (subtree.id === selection.anchorNodeId) {
    const figureNode = (getChildren(subtree) || [])[selection.anchorOffset];
    if (figureNode && isFigure(figureNode)) return figureNode;
  }
  return null;
};
